import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, map, shareReplay, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { Comment } from '@go-modules/interfaces/comments/comment';
import { CommentFilterService } from '@go-modules/services/comment-filter/comment-filter.service';
import { commentModelToken, CommentModel } from 'go-modules/models/comment/comment.factory';
import { COMMENT_RESOURCE_TYPES } from 'go-modules/models/comment/comment.constant';

export interface ProcessedComment extends Comment {
	media: any[];
	markers: any[];
}

@Injectable({
	providedIn: 'root'
})
export class CommentListService implements OnDestroy {
	public filterOutSyncEventComments: boolean = false;
	private commentsSubject = new BehaviorSubject<Comment[]>([]);
	private destroy$ = new Subject<void>();

	public comments$: Observable<ProcessedComment[]> = this.commentsSubject
		.asObservable()
		.pipe(
			map((comments) => this.sortComments(comments)),
			takeUntil(this.destroy$),
			shareReplay(1)
		);

	public emojiFilter = (e: string): boolean => {
		return ![
			'1F972', '1F978', '1F977', '1FAC0', '1FAC1', '1F90C',
			'1F9AC', '1F9A3', '1F9AB', '1F9A4', '1FAB6', '1F9AD',
			'1FAB2', '1FAB1', '1FAB0', '1FAB3', '1FAB4', '1FAD0',
			'1FAD2', '1FAD1', '1FAD3', '1FAD5', '1FAD4', '1FAD6',
			'1F9CB', '1FA84', '1FA86', '1FA85', '1FAA2', '1FAA1',
			'1F6D6', '1FAB5', '1FAA8', '1F6FC', '1F6FB', '1FA96',
			'1FA98', '1FA97', '1FA9D', '1FA9B', '1FA9A', '1FA83',
			'1FA9C', '1F6D7', '1FA9E', '1FA9F', '1FAA4', '1FAA5',
			'1FAA3', '1FAA0', '1FAA7', '1FAA6', '1FAC2', '1FA99'
		].includes(e);
	};

	constructor (
		public commentFilterService: CommentFilterService,
		@Inject(commentModelToken) private Comment: ReturnType<typeof CommentModel>
	) {}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.commentsSubject.complete();
	}

	public toggleFilterOutSyncEventComments (value?: boolean) {
		if (value !== undefined) {
			this.filterOutSyncEventComments = value;
		} else {
			this.filterOutSyncEventComments = !this.filterOutSyncEventComments;
		}

		const savedFeedbackSettings = localStorage.getItem('feedbackSettings');
		const feedbackSettings = savedFeedbackSettings ? JSON.parse(savedFeedbackSettings) : {};
		feedbackSettings.filterOutSyncEventComments = this.filterOutSyncEventComments;
		localStorage.setItem('feedbackSettings', JSON.stringify(feedbackSettings));

		this.filterChanged();
	}

	public shouldFilterOutSyncEventComments (): boolean {
		return this.filterOutSyncEventComments;
	}

	public get currentComments (): Comment[] {
		return this.commentsSubject.getValue();
	}

	public addComment (comment: Comment): void {
		this.commentsSubject.next([...this.currentComments, comment]);
	}

	public addCommentReply (parentComment: Comment, childComment: Comment): void {
		const comments = [...this.currentComments];
		const parent = comments.find((c) => c.comment_id === parentComment.comment_id);
		if (parent) {
			parent.children.push(childComment);
			this.commentsSubject.next(comments);
		}
	}

	public editComment (comment: Comment, updatedComment: Partial<Comment>): void {
		const comments = [...this.currentComments];
		const updated = comments.map((c) => {
			if (comment.parent_id && c.comment_id === comment.parent_id) {
				c.children = c.children.map((child) =>
					child.comment_id === comment.comment_id ? { ...child, ...updatedComment } : child
				);
				return this.Comment.newInstance(c);
			}
			return c.comment_id === comment.comment_id ? this.Comment.newInstance({ ...c, ...updatedComment }) : c;
		});
		this.commentsSubject.next(updated);
	}

	public replaceComment (id: number|string, updatedComment: Comment): void {
		const comments = [...this.currentComments];
		const updated = comments.map((c) => {
			if (updatedComment.parent_id && c.comment_id === updatedComment.parent_id) {
				c.children = c.children.map((child) =>
					child.comment_id === id ? updatedComment : child
				);
				return c;
			}
			return c.comment_id === id ? updatedComment : c;
		});
		this.commentsSubject.next(updated);
	}

	public removeComment (comment: Comment): void {
		const comments = [...this.currentComments];
		const filtered = comments.filter((c) => {
			if (comment.parent_id && c.comment_id === comment.parent_id) {
				c.children = c.children.filter((child) => child.comment_id !== comment.comment_id);
				return true;
			}
			return c.comment_id !== comment.comment_id;
		});
		this.commentsSubject.next(filtered);
	}

	public replaceTempComment (id: string, updatedComment: Comment): void {
		const comments = [...this.currentComments];
		const updated = comments.map((c) => {
			if (updatedComment.parent_id && c.comment_id === updatedComment.parent_id) {
				c.children = c.children.map((child) =>
					child.temp_id === id ? updatedComment : child
				);
				return c;
			}
			return c.temp_id === id ? updatedComment : c;
		});
		this.commentsSubject.next(updated);
	}

	public removeTempComment (comment: Comment): void {
		const comments = [...this.currentComments];
		const filtered = comments.filter((c) => {
			if (comment.parent_id && c.comment_id === comment.parent_id) {
				c.children = c.children.filter((child) => child.temp_id !== comment.temp_id);
				return true;
			}
			return c.temp_id !== comment.temp_id;
		});
		this.commentsSubject.next(filtered);
	}

	public setComments (comments: Comment[]): void {
		this.commentsSubject.next(comments);
	}

	public filterChanged (): void {
		this.commentsSubject.next(this.currentComments);
	}

	private sortComments (comments: Comment[]): ProcessedComment[] {
		const sorted = [...comments].sort((a, b) => {
			if (a.time === null) return 1;
			if (b.time === null) return -1;
			if (a.time === b.time) {
				return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
			}
			return Number(a.time) - Number(b.time);
		});

		const filtered = sorted.filter((c) =>
			this.commentFilterService.shouldCommentShow(c, this.filterOutSyncEventComments)
		);

		const flattened = filtered.flatMap((c) => [
			c,
			...(c.children || []).sort((a, b) =>
				new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
			)
		]);

		return flattened.map((c) => this.processCommentResources({ ...c }));
	}

	private processCommentResources (comment: Comment): ProcessedComment {
		const processed = comment as ProcessedComment;
		processed.markers = [];
		processed.media = [];

		if (comment.resource && comment.resource.length) {
			const markers = [];
			const media = [];

			for (const res of comment.resource) {
				if (res.resource_type === COMMENT_RESOURCE_TYPES.TAG) {
					markers.push(res.item);
				} else if (res.resource_type === COMMENT_RESOURCE_TYPES.MEDIA) {
					media.push(res);
				}
			}

			processed.markers = markers;
			processed.media = media;
		}

		return processed;
	}
}
